// @ts-nocheck
import type { ChangeEvent } from 'react';

import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAuth0 } from '@auth0/auth0-react';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import {
  Box,
  Typography,
  Grid,
  FormControlLabel,
  FormHelperText,
  Radio,
  RadioGroup,
} from '@mui/material';
import { useFormik } from 'formik';
import moment from 'moment';
import * as Yup from 'yup';
import { toast } from 'react-toastify';

import { getTimezoneByZipCode } from '../../helpers';

export const DEFAULT_TZ_MESSAGE = 'Timezone undetermined';

import {
  FHAntdDateTimePicker,
  FHAutocomplete,
  FBoxContainer,
  FHButton,
  FHInputField,
  FProgressBar,
  FHPageLink,
} from '../../components/base';
import { facilityActions, clientActions, cohortActions } from '../../store';
import { cleanNumericInputValue, disabledFutureDate } from '../../helpers';
import {
  FORTA_BLUE,
  FORTA_ERROR,
  FORTA_ORANGE,
  FORTA_SUCCESS,
  FORTA_WHITE,
} from '../../utils/colors';
import { trimsValuesInObject } from '../../utils/common';
import PageContainer from '../../components/layout/PageContainer';
import { FacilityAssignmentType, STANDARD_ERROR_MESSAGES } from '../../constants';
import { ClinicalStatus, DEFAULT_BILLING_FACILITY_LABEL } from '../constants/general';

import type { Moment } from 'moment';
import type { SelectChangeEvent } from '@mui/material';
import type { RootState } from '../../store';
import type { IFacility } from '../../utils/types';

function AddClientForm() {
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();

  const { required } = STANDARD_ERROR_MESSAGES;
  const { list: facilities = [], loading: facilityLoading } = useSelector(
    (state: RootState) => state.facilities,
  );

  const [selectedBillingFacility, setSelectedBillingFacility] = useState<IFacility | null>(null);
  const [selectedServiceFacility, setSelectedServiceFacility] = useState<IFacility | null>(null);

  useEffect(() => {
    dispatch(facilityActions.getList({ getAccessTokenSilently }));
    dispatch(cohortActions.getList({ getAccessTokenSilently }));
  }, [dispatch, getAccessTokenSilently]);

  // Select default billing and service facility
  useEffect(() => {
    if (!facilities.length) return;

    const defaultFacilityOption = facilities.find(
      (item: IFacility) =>
        item.label?.toLowerCase().trim() === DEFAULT_BILLING_FACILITY_LABEL.toLowerCase().trim(),
    );

    if (!selectedBillingFacility && defaultFacilityOption) {
      setSelectedBillingFacility(defaultFacilityOption);
      setFieldValue('billing_facility_id', defaultFacilityOption.id);
    }

    if (!selectedServiceFacility && defaultFacilityOption) {
      setSelectedServiceFacility(defaultFacilityOption);
      setFieldValue('service_facility_id', defaultFacilityOption.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facilities]);

  const clientValidationSchema = Yup.object().shape({
    first_name: Yup.string().trim().required(required),
    last_name: Yup.string().trim().required(required),
    sex: Yup.string().trim().required(required),
    dob: Yup.string().trim().required(required),
    street_address_line_one: Yup.string().trim().required(required),
    street_address_city: Yup.string().trim().required(required),
    street_address_state: Yup.string().trim().required(required),
    street_address_zip: Yup.string().min(5).max(5).required(),
    billing_facility_id: Yup.number().nullable(),
    service_facility_id: Yup.number().nullable(),

    // Custom validation rule
    facilities: Yup.mixed().test('facilities-selected', 'Select both or none', function () {
      const { billing_facility_id, service_facility_id } = this.parent;
      if (
        (billing_facility_id && !service_facility_id) ||
        (!billing_facility_id && service_facility_id)
      ) {
        return false; // Both facilities must be selected or none
      }
      return true;
    }),
  });

  const createClient = async (values: any) => {
    const trimedValues = trimsValuesInObject(values);
    try {
      const createClientResponse = await dispatch(
        clientActions.createClient({
          data: trimedValues,
          getAccessTokenSilently,
        }),
      );
      if (createClientResponse?.error) {
        toast.error(createClientResponse.error);
      }

      return createClientResponse?.payload?.id;
    } catch {
      toast.error('An error occurred while creating client');
      return null;
    }
  };

  const assignFacilityToClient = async (
    clientID: number,
    facilityId: number,
    assignmentType: string,
  ) => {
    try {
      const assignFacilityResponse = await dispatch(
        clientActions.assignFacility({
          data: {
            client_id: clientID,
            date_start: moment().format('YYYY-MM-DD'),
            date_end: null,
            facility_id: facilityId,
            assignment_type: assignmentType,
          },
          getAccessTokenSilently,
        }),
      );
      if (assignFacilityResponse?.error) {
        toast.error(assignFacilityResponse.error);
      }
    } catch {
      toast.error('An error occurred while assigning facility');
      return null;
    }
  };

  const formik = useFormik({
    initialValues: {
      first_name: '',
      last_name: '',
      middle_name: '',
      dob: null,
      sex: '',
      street_address_line_one: '',
      street_address_line_two: '',
      street_address_city: '',
      street_address_state: '',
      street_address_zip: '',
      billing_facility_id: null,
      service_facility_id: null,
    },

    onSubmit: async (values) => {
      // Create client and get client ID from payload
      const clientID = await createClient(values);

      if (clientID) {
        //Assign facilities to client if both facilities are selected
        if (values.billing_facility_id && values.service_facility_id) {
          try {
            // Assign both facilities synchronously
            await Promise.all([
              assignFacilityToClient(
                clientID,
                values.billing_facility_id,
                FacilityAssignmentType.BILLING,
              ),
              assignFacilityToClient(
                clientID,
                values.service_facility_id,
                FacilityAssignmentType.SERVICE,
              ),
            ]);
            // Reset form and selected facilities after successful assignment
            formik.resetForm();
            setSelectedBillingFacility(null);
            setSelectedServiceFacility(null);
            dispatch(
              clientActions.getList({
                getAccessTokenSilently,
                clinical_status: [
                  ClinicalStatus.CLINICAL,
                  ClinicalStatus.PRE_CLINICAL,
                  ClinicalStatus.OFFBOARDED,
                  ClinicalStatus.ON_HOLD,
                ],
              }),
            );
          } catch {
            toast.error('An error occurred while assigning facilities');
          }
        }
      }
    },
    validationSchema: clientValidationSchema,
  });
  const { values, setFieldValue, submitForm, errors, touched } = formik;

  const handleUpdateFieldValues = (key: string, value: any) => {
    const formattedValue = value;
    setFieldValue(key, formattedValue);
  };

  let zone = DEFAULT_TZ_MESSAGE;
  const identifiedTz = getTimezoneByZipCode(values['street_address_zip']);
  if (identifiedTz) {
    zone = identifiedTz;
  }

  return (
    <PageContainer>
      {facilityLoading && <FProgressBar />}
      <Box sx={{ mt: 3 }}>
        <FHPageLink to="/admin" label="Admin Home" />
        <FBoxContainer sx={{ borderRadius: '20px', boxShadow: 'none' }}>
          <form
            data-testid="addClientForm"
            onSubmit={formik.handleSubmit}
            style={{ width: '100%' }}
          >
            <Grid container>
              <Grid item xs={12} md={12} lg={10}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  <Box
                    ml={1}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      flexGrow: 1,
                      justifyContent: 'space-between',
                    }}
                  >
                    <Typography textAlign="left" variant="h4">
                      Add Client
                    </Typography>
                    <Typography fontSize={14} textAlign="right" alignSelf={'flex-end'}>
                      <span style={{ color: 'red', fontSize: 16 }}>*</span>indicates mandatory
                      fields
                    </Typography>
                  </Box>
                  <Box
                    sx={{ width: '100%', borderBottom: 1, borderColor: 'divider', mt: 1, mb: 2 }}
                  />
                  <Grid container p={0.5} spacing={1} display={'flex'} alignItems={'center'}>
                    <Grid item xs={2.5}>
                      <Typography textAlign={'left'}>
                        First Name
                        <span style={{ color: 'red' }}>*</span>
                      </Typography>
                    </Grid>
                    <Grid item xs={8}>
                      <FHInputField
                        inputHeight={40}
                        inputProps={
                          {
                            'data-testid': 'clientFirstName',
                          }
                        }
                        sx={{ boxShadow: 'none', width: '100%' }}
                        value={values['first_name']}
                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                          handleUpdateFieldValues('first_name', e.target.value)
                        }
                        error={touched.first_name && Boolean(errors.first_name)}
                        helperText={touched.first_name && errors.first_name}
                      />
                    </Grid>
                  </Grid>
                  <Grid container p={0.5} spacing={1} display={'flex'} alignItems={'center'}>
                    <Grid item xs={2.5}>
                      <Typography textAlign={'left'}>
                        Last Name<span style={{ color: 'red' }}>*</span>
                      </Typography>
                    </Grid>
                    <Grid item xs={8}>
                      <FHInputField
                        inputHeight={40}
                        inputProps={
                          {
                            'data-testid': 'clientLastName',
                          }
                        }
                        sx={{ boxShadow: 'none', width: '100%' }}
                        value={values['last_name']}
                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                          handleUpdateFieldValues('last_name', e.target.value)
                        }
                        error={touched.last_name && Boolean(errors.last_name)}
                        helperText={touched.last_name && errors.last_name}
                      />
                    </Grid>
                  </Grid>
                  <Grid container p={0.5} spacing={1} display={'flex'} alignItems={'center'}>
                    <Grid item xs={2.5}>
                      <Typography textAlign={'left'}>Middle Name:</Typography>
                    </Grid>
                    <Grid item xs={8}>
                      <FHInputField
                        inputHeight={40}
                        inputProps={
                          {
                            'data-testid': 'clientMiddleName',
                          }
                        }
                        sx={{ boxShadow: 'none', width: '100%' }}
                        value={values['middle_name']}
                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                          handleUpdateFieldValues('middle_name', e.target.value)
                        }
                      />
                    </Grid>
                  </Grid>
                  <Grid container p={0.5} spacing={1} display={'flex'} alignItems={'center'}>
                    <Grid item xs={2.5}>
                      <Typography textAlign={'left'}>
                        DOB<span style={{ color: 'red' }}>*</span>
                      </Typography>
                    </Grid>
                    <Grid item xs={8}>
                      <FHAntdDateTimePicker
                        inputHeight={40}
                        data-testid="clientDOB"
                        format="MM/DD/YYYY"
                        sx={{ boxShadow: 'none', width: '100%' }}
                        disabledDate={disabledFutureDate}
                        error={touched.dob && Boolean(errors.dob)}
                        helperText={<span>{touched.dob && required}</span>}
                        allowClear={false}
                        value={values['dob'] ? moment(values['dob']) : null}
                        onSelect={(value: Moment) =>
                          handleUpdateFieldValues('dob', value.format('YYYY-MM-DD'))
                        }
                        onChange={(value: Moment) =>
                          handleUpdateFieldValues('dob', value.format('YYYY-MM-DD'))
                        }
                      />
                      {touched.dob && Boolean(errors.dob) && (
                        <FormHelperText
                          sx={{ color: FORTA_ERROR, fontSize: '14px', textAlign: 'right' }}
                        >
                          {errors.dob}
                        </FormHelperText>
                      )}
                    </Grid>
                  </Grid>
                  <Grid container p={0.5} spacing={1} display={'flex'} alignItems={'center'}>
                    <Grid item xs={2.5}>
                      <Typography textAlign={'left'}>
                        Gender<span style={{ color: 'red' }}>*</span>
                      </Typography>
                    </Grid>
                    <Grid item xs={8}>
                      <RadioGroup
                        data-testid="clientGender"
                        row
                        value={values['sex']}
                        name="gender-radio-buttons-group"
                        onChange={(e: SelectChangeEvent<unknown>) =>
                          handleUpdateFieldValues('sex', e.target.value)
                        }
                      >
                        <FormControlLabel
                          data-testid="clientGenderMale"
                          value={'male'}
                          control={<Radio />}
                          label="Male"
                        />
                        <FormControlLabel value={'female'} control={<Radio />} label="Female" />
                      </RadioGroup>
                      {touched.sex && Boolean(errors.sex) && (
                        <FormHelperText sx={{ color: 'red', fontSize: '0.75rem' }}>
                          {errors.sex}
                        </FormHelperText>
                      )}
                    </Grid>
                  </Grid>
                  <Grid container p={0.5} spacing={1} display={'flex'} alignItems={'center'}>
                    <Grid item xs={2.5}>
                      <Typography textAlign={'left'}>
                        Street Address 1<span style={{ color: 'red' }}>*</span>
                      </Typography>
                    </Grid>
                    <Grid item xs={8}>
                      <FHInputField
                        inputHeight={40}
                        inputProps={
                          {
                            'data-testid': 'clientStreetAddress1',
                          }
                        }
                        sx={{ boxShadow: 'none', width: '100%' }}
                        value={values['street_address_line_one']}
                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                          handleUpdateFieldValues('street_address_line_one', e.target.value)
                        }
                        error={
                          touched.street_address_line_one && Boolean(errors.street_address_line_one)
                        }
                        helperText={
                          touched.street_address_line_one &&
                          errors.street_address_line_one
                        }
                      />
                    </Grid>
                  </Grid>
                  <Grid container p={0.5} spacing={1} display={'flex'} alignItems={'center'}>
                    <Grid item xs={2.5}>
                      <Typography textAlign={'left'}>Street Address 2:</Typography>
                    </Grid>
                    <Grid item xs={8}>
                      <FHInputField
                        inputHeight={40}
                        inputProps={
                          {
                            'data-testid': 'clientStreetAddress2',
                          }
                        }
                        sx={{ boxShadow: 'none', width: '100%' }}
                        value={values['street_address_line_two']}
                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                          handleUpdateFieldValues('street_address_line_two', e.target.value)
                        }
                      />
                    </Grid>
                  </Grid>
                  <Grid container p={0.5} spacing={1} display={'flex'} alignItems={'center'}>
                    <Grid item xs={2.5}>
                      <Typography textAlign={'left'}>
                        City<span style={{ color: 'red' }}>*</span>
                      </Typography>
                    </Grid>
                    <Grid item xs={8}>
                      <FHInputField
                        inputHeight={40}
                        inputProps={
                          {
                            'data-testid': 'clientCity',
                          }
                        }
                        sx={{ boxShadow: 'none', width: '100%' }}
                        value={values['street_address_city']}
                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                          handleUpdateFieldValues('street_address_city', e.target.value)
                        }
                        error={touched.street_address_city && Boolean(errors.street_address_city)}
                        helperText={
                          touched.street_address_city && errors.street_address_city
                        }
                      />
                    </Grid>
                  </Grid>
                  <Grid container p={0.5} spacing={1} display={'flex'} alignItems={'center'}>
                    <Grid item xs={2.5}>
                      <Typography textAlign={'left'}>
                        State<span style={{ color: 'red' }}>*</span>
                      </Typography>
                    </Grid>
                    <Grid item xs={8}>
                      <FHInputField
                        inputHeight={40}
                        inputProps={
                          {
                            'data-testid': 'clientState',
                          }
                        }
                        sx={{ boxShadow: 'none', width: '100%' }}
                        value={values['street_address_state']}
                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                          handleUpdateFieldValues('street_address_state', e.target.value)
                        }
                        error={touched.street_address_state && Boolean(errors.street_address_state)}
                        helperText={
                          touched.street_address_state && errors.street_address_state
                        }
                      />
                    </Grid>
                  </Grid>
                  <Grid container p={0.5} spacing={1} display={'flex'} alignItems={'center'}>
                    <Grid item xs={2.5}>
                      <Typography textAlign={'left'}>
                        Zip<span style={{ color: 'red' }}>*</span>
                      </Typography>
                    </Grid>
                    <Grid item xs={8}>
                      <Box sx={{ width: '100%', display: 'flex' }}>
                        <FHInputField
                          inputHeight={40}
                          inputProps={
                            {
                              'data-testid': 'clientZip',
                            }
                          }
                          fullWidth
                          sx={{
                            boxShadow: 'none',
                            width: '100%',
                            borderRadius: '10px 0 0 10px',
                          }}
                          value={values['street_address_zip']}
                          onChange={(e: ChangeEvent<HTMLInputElement>) =>
                            handleUpdateFieldValues(
                              'street_address_zip',
                              cleanNumericInputValue(e.target.value),
                            )
                          }
                          error={touched.street_address_zip && Boolean(errors.street_address_zip)}
                          helperText={
                            touched.street_address_zip && errors.street_address_zip
                          }
                        />
                        <Box
                          sx={{
                            height: 40,
                            width: '100%',
                            backgroundColor: FORTA_WHITE,
                            borderRadius: '0 10px 10px 0',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'not-allowed',
                            borderLeft: `1px solid ${FORTA_BLUE.primary}`,
                          }}
                        >
                          {zone === DEFAULT_TZ_MESSAGE && values['street_address_zip'] && (
                            <WarningAmberRoundedIcon sx={{ color: FORTA_ORANGE, ml: 2 }} />
                          )}
                          {zone !== DEFAULT_TZ_MESSAGE && (
                            <CheckCircleRoundedIcon sx={{ color: FORTA_SUCCESS, ml: 2 }} />
                          )}
                          <Typography ml={1}>{zone.replaceAll('_', ' ')}</Typography>
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                  <Box
                    sx={{ width: '100%', borderBottom: 1, borderColor: 'divider', mt: 3, mb: 2 }}
                  />
                  <Grid container p={1} spacing={1} display={'flex'} alignItems={'center'}>
                    <Grid item xs={2.6}>
                      <Typography textAlign={'left'}>Assign billing facility:</Typography>
                    </Grid>
                    <Grid item xs={8}>
                      <FHAutocomplete
                        inputHeight={40}
                        data-testid="clientAssignBillingFacility"
                        sx={{ boxShadow: 'none', width: '100%' }}
                        placeholder="Select billing facility"
                        isOptionEqualToValue={(option: any, value: any) => option.id === value.id}
                        options={facilities.map((bf: IFacility) => ({
                          id: bf.id,
                          label: `${bf.label}`,
                        }))}
                        value={selectedBillingFacility}
                        onChange={
                          //update selected billing facility and assigned facilities state with the new value as type
                          (event: ChangeEvent<HTMLSelectElement>, newValue: IFacility | null) => {
                            handleUpdateFieldValues('billing_facility_id', newValue?.id);
                            setSelectedBillingFacility(newValue);
                          }
                        }
                        error={Boolean(errors.facilities) && !selectedBillingFacility}
                        helperText={
                          errors.facilities && !selectedBillingFacility
                            ? 'Select both facilities or none'
                            : undefined
                        }
                      />
                    </Grid>
                  </Grid>
                  <Grid container p={1} spacing={1} display={'flex'} alignItems={'center'}>
                    <Grid item xs={2.6}>
                      <Typography textAlign={'left'}>Assign service facility:</Typography>
                    </Grid>
                    <Grid item xs={8}>
                      <FHAutocomplete
                        inputHeight={40}
                        data-testid="clientAssignServiceFacility"
                        sx={{ boxShadow: 'none', width: '100%' }}
                        placeholder="Select service facility"
                        isOptionEqualToValue={(option: any, value: any) => option.id === value.id}
                        options={facilities.map((sf: IFacility) => ({
                          id: sf.id,
                          label: `${sf.label}`,
                        }))}
                        value={selectedServiceFacility}
                        onChange={
                          //update selected service facility and assigned facilities state with the new value as type
                          (event: ChangeEvent<HTMLSelectElement>, newValue: IFacility | null) => {
                            handleUpdateFieldValues('service_facility_id', newValue?.id);
                            setSelectedServiceFacility(newValue);
                          }
                        }
                        error={Boolean(errors.facilities) && !selectedServiceFacility}
                        helperText={
                          errors.facilities && !selectedServiceFacility
                            ? 'Select both facilities or none'
                            : undefined
                        }
                      />
                    </Grid>
                  </Grid>
                </Box>

                <Box ml={1} mt={3} sx={{ display: 'flex' }}>
                  <FHButton
                    data-testid="clientSubmit"
                    onClick={submitForm}
                    disabled={formik.isSubmitting}
                  >
                    Submit
                  </FHButton>
                </Box>
              </Grid>
            </Grid>
          </form>
        </FBoxContainer>
      </Box>
    </PageContainer>
  );
}

export default AddClientForm;
